import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import { FilterPanel } from "../components/FilterPanel";
import { TeamMark } from "../components/GameCard";
import { Sheet } from "../components/Sheet";
import { activeFilters, filtersFromSearch, filtersToSearch, withFilter, type FilterKey } from "../lib/eventsPage";
import { games } from "../lib/format";
import { fetchStats, recordText, winPct, yearSeries, type Record3, type StatsDto } from "../lib/statsPage";
import { FilterChips } from "./Events";

function Split({ label, r }: { label: string; r: Record3 }) {
  const pct = (n: number) => (r.gp ? `${(n / r.gp) * 100}%` : "0%");
  return (
    <div className="grid grid-cols-[72px_minmax(0,1fr)_64px] items-center gap-2.5 text-[13.5px]">
      <span className="text-muted">{label}</span>
      <span className="flex h-2 overflow-hidden rounded bg-line/60" aria-hidden>
        <i className="block bg-good" style={{ width: pct(r.w) }} />
        <i className="block bg-faint" style={{ width: pct(r.t) }} />
        <i className="block bg-bad" style={{ width: pct(r.l) }} />
      </span>
      <span className="text-right tabular-nums">{r.gp ? recordText(r) : "—"}</span>
    </div>
  );
}

/** Stats: the same filters as Events, from the same URL. */
export default function Stats() {
  const [sp, setSp] = useSearchParams();
  const filters = filtersFromSearch(sp);
  const query = filtersToSearch(filters).toString();
  const [dto, setDto] = useState<StatsDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sheet, setSheet] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchStats(filtersFromSearch(new URLSearchParams(query)))
      .then((d) => {
        if (cancelled) return;
        setDto(d);
        setError(null);
      })
      .catch((e: unknown) => !cancelled && setError(e instanceof Error ? e.message : String(e)));
    return () => {
      cancelled = true;
    };
  }, [query]);

  const onChange = (k: FilterKey, v: string) => setSp(filtersToSearch(withFilter(filters, k, v)), { replace: true });
  const onClear = () => setSp(new URLSearchParams(), { replace: true });

  if (error && !dto) return <div className="rounded-lg border border-bad/50 bg-bad/10 p-4 text-sm">Could not load stats: {error}</div>;
  if (!dto) return <p className="text-sm text-muted">Loading…</p>;

  const vmax = Math.max(1, ...dto.top_venues.map(([, n]) => n));
  const years = yearSeries(dto.by_year);
  const ymax = Math.max(1, ...years.map(([, n]) => n));
  const n = activeFilters(filters).length;

  return (
    <section className="grid gap-4">
      <div className="flex flex-wrap items-baseline gap-2.5">
        <h1 className="font-display text-3xl leading-none font-bold uppercase">Stats</h1>
        <span className="text-sm text-muted tabular-nums">
          {dto.shown} of {dto.total}
        </span>
        <button type="button" onClick={() => setSheet(true)} className="ml-auto min-h-10 rounded-lg border border-line px-3 text-sm md:hidden">
          Filters{n > 0 ? ` · ${n}` : ""}
        </button>
      </div>
      <FilterChips dto={dto} filters={filters} onChange={onChange} onClear={onClear} />
      <div className="grid grid-cols-1 gap-6 md:grid-cols-[230px_minmax(0,1fr)] md:items-start">
        <aside className="hidden md:block">
          <FilterPanel options={dto.options} filters={filters} onChange={onChange} />
        </aside>
        {dto.shown === 0 ? (
          <p className="text-sm text-muted">No games match these filters.</p>
        ) : (
          <div className="grid gap-4">
            <div className="grid grid-cols-4 rounded-2xl bg-surface text-center tabular-nums">
              {[
                ["Games", dto.shown],
                ["Leagues", dto.leagues],
                ["Venues", dto.venues],
                ["Years", dto.years],
              ].map(([l, v], i) => (
                <div key={l} className={`px-2 py-3 ${i ? "border-l border-line/60" : ""}`}>
                  <b className="block font-display text-3xl leading-none">{v}</b>
                  <span className="text-[11.5px] tracking-wider text-muted uppercase">{l}</span>
                </div>
              ))}
            </div>

            <div className="grid gap-2.5 rounded-2xl bg-surface p-3.5">
              <div className="flex items-center gap-2.5">
                <TeamMark team={dto.team} logo={dto.team_logo ?? undefined} />
                <span className="text-[11px] font-semibold tracking-[0.12em] text-faint uppercase">{dto.team} record</span>
              </div>
              <div className="flex flex-wrap items-baseline gap-3">
                <span className="font-display text-5xl leading-none font-bold tabular-nums">{recordText(dto.record.overall)}</span>
                <span className="text-muted tabular-nums">
                  {winPct(dto.record.overall)} · {games(dto.record.overall.gp)}
                </span>
              </div>
              <div className="grid gap-1.5">
                <Split label="At home" r={dto.record.home} />
                <Split label="On road" r={dto.record.away} />
              </div>
              <p className="text-xs text-faint">Games with a final score that match these filters.</p>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {dto.venue ? (
                <div className="rounded-2xl bg-surface p-3.5">
                  <h2 className="mb-2.5 font-display text-lg leading-none font-bold uppercase">{dto.venue.name}</h2>
                  <p className="mb-2 text-sm text-muted">
                    {dto.venue.city ? `${dto.venue.city} · ` : ""}
                    {games(dto.venue.games)} here
                  </p>
                  <table className="w-full text-[13.5px]">
                    <tbody>
                      {dto.venue.teams_seen.map(([t, c]) => (
                        <tr key={t} className="border-b border-line/60">
                          <td className="py-1.5">{t}</td>
                          <td className="py-1.5 text-right tabular-nums">{c}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="rounded-2xl bg-surface p-3.5">
                  <h2 className="mb-2.5 font-display text-lg leading-none font-bold uppercase">Top venues</h2>
                  {dto.top_venues.map(([v, c]) => (
                    <div key={v} className="grid grid-cols-[minmax(0,150px)_minmax(0,1fr)_32px] items-center gap-2 py-1 text-[13.5px]">
                      <span className="truncate">{v}</span>
                      <span className="h-2 overflow-hidden rounded bg-line/60">
                        <i className="block h-full bg-accent/80" style={{ width: `${(c / vmax) * 100}%` }} />
                      </span>
                      <span className="text-right text-muted tabular-nums">{c}</span>
                    </div>
                  ))}
                </div>
              )}
              <div className="rounded-2xl bg-surface p-3.5">
                <h2 className="mb-2.5 font-display text-lg leading-none font-bold uppercase">By year</h2>
                <div className="flex h-32 items-end gap-0.5 border-b border-line" role="img" aria-label="Games per year">
                  {years.map(([y, c]) => (
                    <i key={y} title={`${y}: ${c}`} className="block min-h-px flex-1 rounded-t-sm bg-accent/80" style={{ height: `${(c / ymax) * 100}%` }} />
                  ))}
                </div>
                <div className="mt-1 flex justify-between text-[11px] text-faint">
                  <span>{years[0]?.[0]}</span>
                  <span>{years[years.length - 1]?.[0]}</span>
                </div>
                <h2 className="mt-4 mb-1.5 font-display text-lg leading-none font-bold uppercase">By league</h2>
                <table className="w-full text-[13.5px]">
                  <tbody>
                    {dto.by_league.map((r) => (
                      <tr key={r.league} className="border-b border-line/60">
                        <td className="py-1.5">
                          {r.emoji} {r.league}
                        </td>
                        <td className="py-1.5 text-right tabular-nums">{r.games}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
      {sheet && (
        <Sheet title="Filters" onClose={() => setSheet(false)}>
          <div className="grid gap-4">
            <FilterPanel options={dto.options} filters={filters} onChange={onChange} />
            <button type="button" onClick={() => setSheet(false)} className="min-h-10 justify-self-end rounded-lg bg-neutral-100 px-3.5 text-sm font-semibold text-ground">
              Done
            </button>
          </div>
        </Sheet>
      )}
    </section>
  );
}
